import { setPreference } from '@fs/zion-preferences'
import { useHistory } from '@fs/zion-router'
import { LayoutBand, Paragraph } from '@fs/zion-ui'
import React, { useEffect, useRef, useState } from 'react'
import { useDetailsOptInOutParams } from './detailsOptInOutRouteUtil'
import CenteredLoadingSpinner from '../../common/CenteredLoadingSpinner'
import { localStorageRemoveItem } from '../../utils/localStorage'

const REDIRECT_COUNTDOWN_SECONDS = 5

const SOFT_LAUNCH_PREF = 'softLaunch.treeWeb_personR9_sourceCentricDetails'
const SOURCE_CENTRIC_FLAG_CACHE_KEY = 'tree-person.sourceCentricDetails'

export default function SourceCentricDetailsOptInOut(): JSX.Element {
  const { action, pid } = useDetailsOptInOutParams()
  const history = useHistory()
  const hasRun = useRef(false)
  const [message, setMessage] = useState('')
  const [isError, setIsError] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_COUNTDOWN_SECONDS)

  useEffect(() => {
    if (hasRun.current) return
    hasRun.current = true

    const optIn = action === 'opt-in'
    // opt-in sets true so the banner shows; opt-out clears ('') so the banner is hidden
    setPreference(SOFT_LAUNCH_PREF, optIn ? true : '')
      .then(() => {
        // Drop the cached flag so the full-page redirect below re-resolves the source-centric flag
        // from the new pref; otherwise useSourceCentricDetailsFlag would hydrate the stale treatment
        // and the subnav/layout theme would not flip to match the experiment.
        localStorageRemoveItem(SOURCE_CENTRIC_FLAG_CACHE_KEY)
        setMessage(
          optIn
            ? 'You have been opted in to the new details experience.'
            : 'You have been opted out of the new details experience.'
        )
        return undefined
      })
      .catch(() => {
        setIsError(true)
        setMessage(
          optIn
            ? 'There was an error opting in to the new details experience. Please try again later.'
            : 'There was an error opting out of the new details experience. Please try again later.'
        )
      })
  }, [action])

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined

    if (message) {
      if (secondsLeft === 0) {
        // Full-page navigation (not history.push) so the source-centric flag re-resolves from the
        // updated pref and the subnav/layout theme reflects the new opt-in/opt-out state. createHref
        // prepends the router basename that a bare window.location path would otherwise drop.
        window.location.assign(history.createHref({ pathname: `/details${pid ? `/${pid}` : ''}` }))
      } else {
        timer = setTimeout(() => setSecondsLeft((prev) => prev - 1), 1000)
      }
    }

    return () => clearTimeout(timer)
  }, [message, secondsLeft, history, pid])

  return (
    <LayoutBand>
      {!isError && <CenteredLoadingSpinner />}
      {message && (
        <>
          <Paragraph centered color={isError ? 'red' : undefined}>
            {message}
          </Paragraph>
          <Paragraph centered>
            Redirecting to details in {secondsLeft} second{secondsLeft !== 1 ? 's' : ''}...
          </Paragraph>
        </>
      )}
    </LayoutBand>
  )
}
